// The permanent middle-man between an ordered product's component demand and a purchase request. One
// requisition is queued per hardware-item-per-ordered-product on the planning view, then
// requisitions are combined into purchase requests on the queue view. It is the link that lets us
// trace both ways: ordered product -> its requisitions -> purchase request ids, and purchase
// request -> its requisitions -> ordered product ids.
//   - quantity is the USER-CONFIRMED amount to order, not the calculated need: on-hand counts are
//     unreliable and users deliberately over-order a buffer (or under-order when stock covers the
//     rest), so the BOM need is only ever a pre-filled suggestion.
//   - neededQuantity is a snapshot of the live BOM need, rewritten on every create/update. When the
//     order changes afterwards the live need drifts from this snapshot, which is how the planning
//     view can say "was 160 when queued" next to its stale flag.
//   - groupKey is an opaque generated label (NOT a document id — nothing else references it) that
//     pins requisitions into the same proposed request on the queue view. '' means "follow the
//     automatic due-date bundling"; it is only set when the user manually splits/moves/merges, so
//     untouched bundles keep recomputing as orders shift. Kept separate from purchaseRequestId so
//     grouping survives a request cancel.
//   - purchaseRequestId is '' while queued. Creating a purchase request from a bundle stamps each
//     requisition with the real purchase request id; the requisition is kept, not deleted. Canceling
//     the purchase request resets its requisitions back to '' so the unmet demand returns to the
//     queue.
//   - noOrderNeeded records the OTHER decision a planner can make: "I looked at this component and
//     stock already covers it, order nothing". Without it an untouched component and a deliberately
//     skipped one are indistinguishable — both simply have no requisition — so the next person can't
//     tell what was considered from what was missed. A skipped requisition carries quantity 0 and
//     never reaches the queue or a purchase request; neededQuantity still snapshots the need at the
//     moment of the decision, so the planning view can flag the skip when the order later grows past
//     what was reviewed.
export interface PlasticsPurchaseRequisition {
    id: string;
    hardwareId: string;
    plasticsOrderedProductId: string;
    quantity: number;
    neededQuantity: number;
    groupKey: string;
    purchaseRequestId: string;
    noOrderNeeded: boolean;
}

export const DEFAULT_PLASTICS_PURCHASE_REQUISITION: PlasticsPurchaseRequisition = {
    id: '',
    hardwareId: '',
    plasticsOrderedProductId: '',
    quantity: 0,
    neededQuantity: 0,
    groupKey: '',
    purchaseRequestId: '',
    noOrderNeeded: false,
};
